import axios from 'axios'
import { mapGetters } from 'vuex'

export default {
  data () {
    return {
      ajaxLoading: {
        show: false,
        cancelToken: ''
      },
      peerConnection: null,
      configuration: {
        iceServers: [
          { url: 'stun:stun.1.google.com:19302' }
        ],
        iceCandidatePoolSize: 2
      },
      channel: null,
      offer: null,
      message: null,
      logout: false,
      reconnectAttempts: 0,
      maxReconnectAttempts: 5,
      reconnectDelay: 2000 // 2 seconds
    }
  },
  computed: {
    ...mapGetters([
      'posIsConnected',
      'terminalId'
    ]),
    key () {
      return this.form.inputCode || localStorage.getItem('terminalId')
    }
  },
  methods: {
    async clickOfferPasted (cancel = false) {
      if (cancel) {
        this.ajaxLoading.cancelToken.cancel()
        this.modalShow = false
        return false
      }

      const self = this
      await this.getMessage().then(async (offer) => {
        await this.createPeerConnection()
        await this.peerConnection.setRemoteDescription(offer)
          .then(() => {
            console.log('Remote connected!')
            this.peerConnection.restartIce()
          },
          (error) => { console.log('Remote connection failed!', error) })
        await this.peerConnection.createAnswer().then(async (answer) => {
          this.modalShow = false
          try {
            await self.peerConnection.setLocalDescription(answer)
          } catch (e) {
            console.log('setLocalDescriptionFailed', e)
          }
        }).then(async () => {
          this.clearNum()
        }).catch((e) => console.log('ErrorSetConnection', e))
      }).catch((e) => console.log('ErrorGetMessage', e))
    },

    async handleReconnection () {
      if (this.reconnectAttempts < this.maxReconnectAttempts) {
        console.log('recoonect attempt: ', this.reconnectAttempts)
        this.reconnectAttempts++
        setTimeout(() => {
          this.clickOfferPasted() // Re-initiate the connection
        }, this.reconnectDelay)
      } else {
        console.log('Max reconnect attempts reached. Giving up.')
      }
    },

    createPeerConnection () {
      if (this.peerConnection) {
        this.peerConnection.close() // Close existing connection
        this.peerConnection = null
      }
      try {
        this.peerConnection = new RTCPeerConnection(this.configuration)
        this.peerConnection.onconnectionstatechange = async () => {
          console.log('connectionState:', this.peerConnection.connectionState)
          if (this.peerConnection.connectionState === 'connected') {
            this.reconnectAttempts = 0 // Reset reconnect attempts on successful connection
          } else if (this.peerConnection.connectionState === 'disconnected' || this.peerConnection.connectionState === 'failed' || this.peerConnection.connectionState === 'new') {
            this.handleReconnection()
          }
        }
        this.peerConnection.ondatachannel = this.handleDataChannel
        this.peerConnection.onicecandidate = this.iceCallback
      } catch (err) {
        console.log('error: ' + err)
      }
    },

    async getMessage () {
      this.ajaxLoading.show = true
      this.ajaxLoading.cancelToken = axios.CancelToken.source()

      return new Promise(async (resolve, reject) => {
        const domainName = this.form.domainName || localStorage.getItem('domainName')
        const messageURL = `https://${domainName}.biyo.co/api/customer-display/get-sync-message/`

        if (!this.key) {
          console.error('Cannot get message: key (terminal ID) is missing')
          this.ajaxLoading.show = false
          reject(new Error('Terminal ID is missing'))
          return
        }

        await axios.post(messageURL, { 'key': this.key },
          { cancelToken: this.ajaxLoading.cancelToken.token }
        ).then(async response => {
          this.ajaxLoading.show = false
          if (response.data && response.data['offer']) {
            resolve(JSON.parse(response.data['offer']))
          } else {
            console.error('No offer data received from POS')
            reject(new Error('No offer data'))
          }
        })
          .catch(err => {
            console.error('Failed to get message:', (err.response && err.response.data) || err.message)
            this.ajaxLoading.show = false
            if (err.message && !axios.isCancel(err)) {
              this.$store.dispatch('setPosIsConnected', false)
              if (err.response && err.response.data && err.response.data.hasOwnProperty('key')) {
                this.showAlert(false, 'Domain Name or Terminal Number is invalid')
                this.clearNum()
              } else {
                this.showAlert(false, 'Could not connect to POS. Please try again.')
              }
              reject(err)
            }
          })
      })
    },
    async connect () {
      console.log('Start Send ANSWER')
      await this.rewriteMessage()
    },
    async iceCallback (event) {
      if (event.candidate) {
        console.log('new ice candidate', event.candidate)
      } else {
        await this.connect()
        console.log('finish ice candidate')
      }
    },
    onAddIceCandidateSuccess () {
      console.log('AddIceCandidate success.')
    },
    onAddIceCandidateError (error) {
      console.log('Failed to add Ice Candidate: ' + error.toString())
    },
    handleDataChannel (event) {
      this.channel = event.channel
      this.channel.onopen = () => {
        this.status = true
        this.$store.dispatch('setPosIsConnected', true)
        // this.showAlert() // Disabled - popup is distracting
      }
      this.channel.onmessage = this.datachannelmessage
      this.channel.onerror = (error) => {
        console.log('Error:', error)
      }
      this.channel.onclose = () => {
        if (!this.logout) {
          this.$store.dispatch('setPosIsConnected', false)
          this.showAlert(false)
        }
        this.logout = false
      }
    },
    async rewriteMessage () {
      const domainName = this.form.domainName || localStorage.getItem('domainName')
      const rewriteMessageUrl = `https://${domainName}.biyo.co/api/customer-display/rewrite-sync-message/`

      if (!this.key) {
        console.error('Cannot send answer: key (terminal ID) is missing')
        return
      }

      if (!this.peerConnection || !this.peerConnection.localDescription) {
        console.error('Cannot send answer: peer connection or local description is missing')
        return
      }

      try {
        await axios.post(rewriteMessageUrl, {
          'key': this.key,
          'message': JSON.stringify(this.peerConnection.localDescription)
        })
        console.log('Answer sent successfully')
      } catch (error) {
        console.error('Failed to send answer:', (error.response && error.response.data) || error.message)
        // Don't throw - let the connection attempt continue
      }
    },
    disconnectPeerConnection () {
      if (this.posIsConnected) {
        this.logout = true
        if (this.channel) {
          this.channel.send(JSON.stringify({logout: this.logout}))
        }

        if (this.peerConnection) {
          this.peerConnection.close()
          this.peerConnection = null
        }

        if (this.pusher) {
          this.pusher.disconnect()
        }

        this.$store.dispatch('setPosIsConnected', false)
        localStorage.removeItem('terminalId')
        localStorage.removeItem('domainName')
      }
    }
  }
}



// WEBPACK FOOTER //
// ./src/mixins/rtcConnection.js